import { CellData, NA_CELL, NO_DATA_CELL, ProgressBar } from './ProgressBar';
import {
  formatDateMMddYYYY,
  MetricProgressBar,
  toProgressSegments,
} from '../utils/utils';
import {
  GoalProgressMetrics,
  ProgressPortraitMetrics,
} from '../model/ProgressPortrait';
import { CellTooltip, TooltipItem } from './CellTooltip';

const extractGoalMetricsData = (
  progressPortraitMetrics: ProgressPortraitMetrics,
) => progressPortraitMetrics.goals;

const toCellData = (isBaseline: boolean) => {
  return (metricData: GoalProgressMetrics): CellData => {
    if (
      !(metricData.reportDate && metricData.clientExhibitDangerousBehaviour)
    ) {
      return NO_DATA_CELL;
    }
    const tooltip = (
      <DangerousBehaviorTooltip goal={metricData} isBaseline={isBaseline} />
    );
    const text = metricData.clientExhibitDangerousBehaviour;
    if (text === 'Yes') {
      return {
        level: 'low',
        label: 'Yes',
        tooltip,
      };
    }
    if (text === 'No') {
      return {
        level: 'high',
        label: 'No',
        tooltip,
      };
    }
    return NA_CELL;
  };
};

const getSign = (text: string): string => {
  if (text === 'Increasing') {
    return '+';
  }
  if (text === 'Decreasing') {
    return '-';
  }
  return 'no change';
};

const tooltipValue = (
  hasValue: boolean,
  frequency: string,
  intensity: string,
) => {
  if (hasValue) {
    return `frequency (${getSign(frequency)}), intensity (${getSign(
      intensity,
    )})`;
  }
  return '';
};

type DangerousBehaviorMetricProps = MetricProgressBar & {
  isBaseline: boolean;
};

export const DangerousBehaviorMetric = ({
  progressPortraitMetrics,
  isBaseline,
}: DangerousBehaviorMetricProps) => {
  const segments = toProgressSegments(
    progressPortraitMetrics,
    extractGoalMetricsData,
    toCellData(isBaseline),
  );

  return <ProgressBar segments={segments} />;
};

const DangerousBehaviorTooltip = ({
  goal,
  isBaseline,
}: {
  goal: GoalProgressMetrics;
  isBaseline: boolean;
}) => {
  if (!goal.clientExhibitDangerousBehaviour) {
    return null;
  }
  const info: TooltipItem[] = [
    {
      label: isBaseline
        ? 'Date of Initial Assessment:'
        : 'Client report received:',
      value: formatDateMMddYYYY(goal.reportDate),
    },
    {
      label: 'Self-injurious behavior:',
      value: tooltipValue(
        goal.hasSelfInjurious,
        goal.selfInjuriousFrequency,
        goal.selfInjuriousIntensity,
      ),
    },
    {
      label: 'Physical harm:',
      value: tooltipValue(
        goal.hasPhysicalHarm,
        goal.physicalHarmFrequency,
        goal.physicalHarmIntensity,
      ),
    },
    {
      label: 'Dangerous elopement:',
      value: tooltipValue(
        goal.hasDangerousElopement,
        goal.dangerousElopementFrequency,
        goal.dangerousElopementIntensity,
      ),
    },
    {
      label: 'Sexually inappropriate behavior:',
      value: tooltipValue(
        goal.hasSexuallyInappropriate,
        goal.sexuallyInappropriateFrequency,
        goal.sexuallyInappropriateFrequency,
      ),
    },
    {
      label: 'Property destruction:',
      value: tooltipValue(
        goal.hasPropertyDestruction,
        goal.propertyDestructionFrequency,
        goal.propertyDestructionIntensity,
      ),
    },
    {
      label: 'Eating food or non-food items:',
      value: tooltipValue(
        goal.hasEatingFoodItems,
        goal.eatingFoodItemsFrequency,
        goal.eatingFoodItemsIntensity,
      ),
    },
    {
      label: 'Behaviors connected to elimination:',
      value: tooltipValue(
        goal.hasConnectedElimination,
        goal.connectedEliminationFrequency,
        goal.connectedEliminationIntensity,
      ),
    },
    {
      label: 'Other behaviors:',
      value: tooltipValue(
        goal.hasOtherBehaviors,
        goal.otherBehaviorsFrequency,
        goal.otherBehaviorsIntensity,
      ),
    },
  ];
  return <CellTooltip info={info} />;
};
